import {Injectable} from '@angular/core';
import {BehaviorSubject, interval, Observable, of, Subscription} from 'rxjs';
import {PaginatedResult, SingleResult} from '@app/interfaces';
import {environment} from '@src/environments/environment';
import { HttpClient } from '@angular/common/http';
import {InAppNotification} from '@app/interfaces/entities/in-app-notification';
import {ArrayResult} from '@app/interfaces/responses/array-result';
import {catchError, map, startWith, switchMap, tap} from 'rxjs/operators';
import {BadgeService} from '@app/services/badge.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationApiService {

  public unreadNotifications = new BehaviorSubject<InAppNotification[]>([]);
  public last5notifications = new BehaviorSubject<InAppNotification[]>([]);
  public loudNotifications = new BehaviorSubject<InAppNotification[]>([]);

  private timeIntervalSubscription: Subscription;
  private consecutiveErrors = 0;
  private maxConsecutiveErrors = 3;
  private fastPollSubscription: Subscription;
  private static readonly FAST_INTERVAL = 5000;

  constructor(
    private http: HttpClient,
    private badgeService: BadgeService
  ) {
  }

  // Gets all unread notifications
  startFetchingNotifications() {
    this.timeIntervalSubscription?.unsubscribe();
    this.consecutiveErrors = 0;

    this.timeIntervalSubscription = interval(30000)
      .pipe(
        startWith(0),
        switchMap(() => this.reloadNotifications().pipe(
          tap(() => this.consecutiveErrors = 0),
          catchError((err) => {
            this.consecutiveErrors++;
            console.log('Notification fetch error', err);

            if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
              console.warn('Stopping notification polling after consecutive failures');
              this.timeIntervalSubscription?.unsubscribe();
            }

            return of(null);
          })
        )),
      )
      .subscribe();
  }

  startFastPolling(maxDurationMs: number = 300000): void {
    this.stopFastPolling();
    this.timeIntervalSubscription?.unsubscribe();

    this.fastPollSubscription = interval(NotificationApiService.FAST_INTERVAL)
      .pipe(
        startWith(0),
        switchMap(() => this.reloadNotifications().pipe(
          catchError((err) => {
            console.log('Fast poll error', err);
            return of(null);
          })
        )),
      )
      .subscribe();

    setTimeout(() => this.stopFastPolling(), maxDurationMs);
  }

  // Kills both polls for good. Nulling the fast-poll subscription also disarms
  // the pending stopFastPolling() timeout, which would otherwise restart the
  // slow poll up to 5 minutes after the session ended 
  stopPolling(): void {
    this.timeIntervalSubscription?.unsubscribe();
    this.timeIntervalSubscription = null;
    this.fastPollSubscription?.unsubscribe();
    this.fastPollSubscription = null;
  }

  stopFastPolling(): void {
    if (this.fastPollSubscription) {
      this.fastPollSubscription.unsubscribe();
      this.fastPollSubscription = null;
      this.startFetchingNotifications();
    }
  }

  reloadNotifications() {
    return this.http.get<ArrayResult<InAppNotification>>(`${environment.apiUrl}/api/notifications/`).pipe(map((response) => {
      const notifications = response.data;

      const unreadNotifications: InAppNotification[] = notifications.filter((notification) => !notification.read_at);
      const last5notifications: InAppNotification[] = notifications.slice(0, 5);
      const loudNotifications: InAppNotification[] = unreadNotifications.filter((notification) => notification.data?.loud === true);

      this.unreadNotifications.next(unreadNotifications);
      this.last5notifications.next(last5notifications);
      this.loudNotifications.next(loudNotifications);

      // Update app badge with unread count
      this.badgeService.setBadge(unreadNotifications.length);

      return notifications;
    }));
  }

  // Sets notifications readed when open the notification
  getAllNotifications(): Observable<ArrayResult<any>> {
    return this.http.get<ArrayResult<any>>(`${environment.apiUrl}/api/notifications/get-all`, {});
  }

  // Sets notifications readed when open the notification
  readAllNotifications(): Observable<SingleResult<any>> {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/notifications/read-all`, {}).pipe(
      tap(() => {
        // Clear badge immediately when all notifications are marked as read
        this.badgeService.clearBadge();
      })
    );
  }

  // Sets the value of isClicked to true
  readNotification(uuid: string): Observable<SingleResult<any>> {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/notifications/read-notification`, {uuid});
  }

  markNotificationRead(id: string) {
    return this.readNotification(id).pipe(switchMap(() => this.reloadNotifications()));
  }

  // Subscribe to push notifications
  subscribePushNotifications(platform: string, token: string): Observable<SingleResult<any>> {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/push-notification/subscribe`, {
      platform,
      token
    });
  }

  // Unsubscribe from push notifications
  unsubscribePushNotifications(token: string): Observable<SingleResult<any>> {
    return this.http.post<SingleResult<any>>(`${environment.apiUrl}/api/push-notification/unsubscribe`, {
      token
    });
  }

}
